import { PORTAL_TYPES } from "./portalConfig.js";

export const PERSONA_STORAGE_KEY = "ftmc.portalPersona";
export const IMPERSONATION_STORAGE_KEY = "ftmc.userImpersonation";
export const IMPERSONATION_QUERY_KEYS = [
  "impersonate_email", "impersonate_admin_email", "impersonate_admin_account",
];

const EMPTY = Object.freeze({ portal: null, personaPortal: null, impersonation: null, owner: null });
let current = EMPTY;
let version = 0;
const listeners = new Set();

export function getPortalSession() {
  return current;
}

export function getPortalSessionVersion() {
  return version;
}

export function subscribePortalSession(listener) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function getPortalSessionOwner(account) {
  return {
    accountId: account?.homeAccountId || "",
    email: String(account?.username || account?.idTokenClaims?.preferred_username || "").trim().toLowerCase(),
  };
}

export function getPortalSessionForAccount(account, session = current) {
  const owner = session.owner;
  const identity = getPortalSessionOwner(account);
  if (!owner) return EMPTY;
  const matches = owner.accountId && identity.accountId
    ? owner.accountId === identity.accountId
    : owner.email && owner.email === identity.email;
  return matches ? session : EMPTY;
}

function publish(next) {
  if (current === next) return;
  current = next;
  version += 1;
  listeners.forEach(listener => listener());
}

export function setPortalSession({ portal, personaPortal = null, impersonation = null, owner }) {
  if (!PORTAL_TYPES.includes(portal) || (!owner?.accountId && !owner?.email)) return null;
  const next = Object.freeze({ portal, personaPortal, impersonation, owner });
  publish(next);
  return next;
}

export function clearPortalSession() {
  if (current.owner) publish(Object.freeze({ ...EMPTY }));
}

// Previous releases persisted persona state and transported impersonation in URLs.
// Discard that state before auth bootstrap. A new document always starts at the
// deployed portal; SPA navigation keeps only this module's in-memory session.
export function discardLegacyPortalSession() {
  if (typeof window === "undefined") return;
  try {
    window.sessionStorage.removeItem(PERSONA_STORAGE_KEY);
    window.sessionStorage.removeItem(IMPERSONATION_STORAGE_KEY);
  } catch { /* Storage is optional; nothing is ever hydrated from it. */ }
  try {
    const url = new URL(window.location.href);
    const hadLegacyParams = IMPERSONATION_QUERY_KEYS.some(key => url.searchParams.has(key));
    IMPERSONATION_QUERY_KEYS.forEach(key => url.searchParams.delete(key));
    if (hadLegacyParams) window.history.replaceState(window.history.state, "", `${url.pathname}${url.search}${url.hash}`);
  } catch { /* Non-browser callers have no URL to clean. */ }
}

discardLegacyPortalSession();
